/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- The labeled overflow region needs keyboard scrolling. */
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuArrowLeft, LuSearch, LuImageOff } from "react-icons/lu";
import { EditorSections } from "../../../../../components/editorSections";
import { AdminInput, AdminTextarea } from "../../../../../components/adminForm";
import {
  getSalesChannels,
  updateSalesChannel,
  toggleChannelActiveStatus,
} from "../../../../../api/salesChannels";
import {
  getAllBusinessHours,
  getPaymentsMethods,
} from "../../../../../api/configuration";
import { getDeliveryMethods } from "../../../../../api/deliveryMethods";
import { getProductsWithOrganization } from "../../../../../api/products";
import {
  requestTableRefresh,
  updateFloatPanel,
} from "../../../../../reducers/userConfigSlice";
import { scheduleLabel, shippingLabel } from "../channelFacts";
import { productPriceWithTax } from "../../products/productListModel";
import { ChannelTerminals } from "./terminals";
import { Details } from "./details";
const on = (value) => value === true || value === 1;
const ids = (row, key, id) => (row[key] || []).map((item) => item[id]);
const draft = (row) => ({
  name: row.name || "",
  identifier: row.identifier || "",
  description: row.description || "",
  phone: row.phone || "",
  email: row.email || "",
  address: row.address || "",
  catalogId: row.catalogId || "",
  businessHoursId: row.businessHoursId || "",
  pricePercentage: String(Number(Number(row.pricePercentage ?? 0).toPrecision(7))),
  paymentMethodIds: ids(row, "paymentMethods", "paymentMethodId"),
  shippingMethodIds: ids(row, "shippingMethods", "shippingMethodId"),
});
const equalIds = (a, b) =>
  JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
export const ChannelEditor = () => {
  const panel = useSelector(
    (state) => state.aplicationConfig.userConfig.floatPanel,
  );
  const catalogs =
    useSelector((state) => state.aplicationConfig.corporationData.catalogs) ||
    [];
  const dispatch = useDispatch();
  const [loaded, setLoaded] = useState(null),
    [error, setError] = useState(""),
    [attempt, setAttempt] = useState(0);
  const [form, setForm] = useState(null),
    [baseline, setBaseline] = useState(null),
    [busy, setBusy] = useState(false),
    [saved, setSaved] = useState(false);
  const [active, setActive] = useState(false),
    [stateUnknown, setStateUnknown] = useState(false),
    [search, setSearch] = useState("");
  const lock = useRef(false),
    formRef = useRef(null);
  const id = panel.data?.salesChannelId || panel.queryId;
  const close = () => {
    if (!lock.current)
      dispatch(
        updateFloatPanel({ visible: false, queryId: null, isEmpty: true }),
      );
  };
  useEffect(() => {
    if (panel.isEmpty) return;
    let current = true;
    setLoaded(null);
    setError("");
    Promise.all([
      getSalesChannels(),
      getAllBusinessHours(),
      getPaymentsMethods(),
      getDeliveryMethods(),
      getProductsWithOrganization(),
    ])
      .then((responses) => {
        if (!current) return;
        if (responses.some((r) => r?.error !== false || !Array.isArray(r.data)))
          throw Error();
        const row = responses[0].data.find(
          (item) => item.salesChannelId === id,
        );
        if (!row) throw Error();
        const initial = draft(row);
        setForm(initial);
        setBaseline(initial);
        setActive(on(row.isActive));
        setStateUnknown(false);
        setLoaded({
          row,
          hours: responses[1].data,
          payments: responses[2].data,
          deliveries: responses[3].data,
          products: responses[4].data,
        });
      })
      .catch(() => {
        if (current)
          setError(
            "No se pudo cargar el canal y su configuración. Reintenta antes de editar.",
          );
      });
    return () => {
      current = false;
    };
  }, [id, panel.isEmpty, attempt]);
  const change = (key, value) => {
    setSaved(false);
    setForm((previous) => ({ ...previous, [key]: value }));
  };
  const save = async (event) => {
    event.preventDefault();
    if (lock.current || !form || !formRef.current.reportValidity()) return;
    lock.current = true;
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const payload = { ...form, pricePercentage: Number(form.pricePercentage), catalogId: form.catalogId || null, businessHoursId: form.businessHoursId || null };
      if ((await updateSalesChannel(id, payload))?.error !== false)
        throw Error();
      const read = await getSalesChannels();
      const row =
        read?.error === false &&
        read.data?.find((item) => item.salesChannelId === id);
      if (!row) throw Error();
      const actual = draft(row);
      if (
        Object.keys(form).some((key) =>
          Array.isArray(form[key])
            ? !equalIds(form[key], actual[key])
            : key === "pricePercentage" ? Math.fround(Number(form[key])) !== Math.fround(Number(actual[key])) : form[key] !== actual[key],
        )
      )
        throw Error();
      setForm(actual);
      setBaseline(actual);
      setLoaded((previous) => ({ ...previous, row }));
      setSaved(true);
      dispatch(requestTableRefresh());
    } catch {
      setError(
        "No se pudo confirmar el guardado completo. Tus cambios se conservan; revisa y reintenta.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const toggleActive = async () => {
    if (lock.current) return;
    setSaved(false);
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      if ((await toggleChannelActiveStatus(id))?.error !== false) throw Error();
      const read = await getSalesChannels();
      const row =
        read?.error === false &&
        read.data?.find((item) => item.salesChannelId === id);
      if (!row || on(row.isActive) === active) throw Error();
      setActive(on(row.isActive));
      dispatch(requestTableRefresh());
    } catch {
      setStateUnknown(true);
      setError(
        "No se pudo confirmar el estado. Cierra y vuelve a abrir el canal antes de cambiarlo otra vez.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const header = (
    <header className="channelReferenceHeading">
      <button
        type="button"
        className="channelBack"
        onClick={close}
        disabled={busy}
      >
        <LuArrowLeft aria-hidden="true" /> Canal de venta
      </button>
      <div>
        <h2 id="admin-editor-title">
          {panel.isEmpty
            ? "Nuevo canal de venta"
            : loaded?.row.name || panel.data?.name || "Canal de venta"}
        </h2>
        {loaded && (
          <div className="channelHeadingActions">
            <span>Estado</span>
            <button
              className="channelStateSwitch"
              type="button"
              role="switch"
              aria-label="Canal activo"
              aria-checked={active}
              onClick={toggleActive}
              disabled={busy || stateUnknown}
            >
              <span aria-hidden="true" />
              {active ? "Activo" : "Inactivo"}
            </button>
            <button
              className="channelPrimary"
              form="channel-reference-form"
              type="submit"
              disabled={
                busy || JSON.stringify(form) === JSON.stringify(baseline)
              }
            >
              {busy ? "Guardando…" : "Guardar"}
            </button>
          </div>
        )}
      </div>
    </header>
  );
  if (panel.isEmpty)
    return (
      <div className="channelReferenceEditor">
        {header}
        <section className="channelCreateCard">
          <Details />
        </section>
      </div>
    );
  if (!loaded || !form)
    return (
      <div className="channelReferenceEditor">
        {header}
        {error ? (
          <p role="alert">
            {error}{" "}
            <button
              type="button"
              onClick={() => setAttempt((value) => value + 1)}
            >
              Reintentar
            </button>
          </p>
        ) : (
          <p role="status">Cargando canal…</p>
        )}
      </div>
    );
  const selectedProducts = loaded.products.filter(
    (p) => form.catalogId && p.catalogIds?.includes(form.catalogId),
  );
  const visibleProducts = selectedProducts.filter((p) =>
    `${p.name || ""} ${p.reference || ""}`
      .toLocaleLowerCase()
      .includes(search.toLocaleLowerCase()),
  );
  const methods = (items, key, idKey) => (
    <div className="channelMethodRows">
      {items.map((item) => {
        const checked = form[key].includes(item[idKey]);
        return (
          <label key={item[idKey]}>
            <input
              type="checkbox"
              checked={checked}
              onChange={() =>
                change(
                  key,
                  checked
                    ? form[key].filter((value) => value !== item[idKey])
                    : [...form[key], item[idKey]],
                )
              }
            />
            <span>
              {key === "paymentMethodIds"
                ? item.name
                : shippingLabel({ shippingMethods: [item] }, loaded.deliveries)}
            </span>
            <small>{checked ? "Admitido" : "No admitido"}</small>
          </label>
        );
      })}
      {!items.length && <p>No hay métodos configurados.</p>}
    </div>
  );
  const schedule = scheduleLabel({ ...loaded.row, businessHoursId: form.businessHoursId }, loaded.hours);
  const sections = [
    {
      id: "information",
      title: "Información básica",
      navTitle: "Información",
      description: "Cómo se identifica este canal de venta.",
      content: (
        <>
          <div className="channelReferenceFields">
            <AdminInput
              id="channel-name"
              label="Nombre"
              required
              value={form.name}
              onChange={(v) => change("name", v)}
              description="Nombre del canal en el panel y en los pedidos."
            />
            <AdminInput
              id="channel-identifier"
              label="Identificador"
              value={form.identifier}
              readOnly
              description="Identificador guardado del canal."
            />
          </div>
          <details className="channelExtra">
            <summary>Datos de contacto y descripción</summary>
            <div className="channelReferenceFields">
              <AdminInput
                id="channel-phone"
                label="Teléfono de contacto"
                type="tel"
                value={form.phone}
                onChange={(v) => change("phone", v)}
              />
              <AdminInput
                id="channel-email"
                label="Correo de contacto"
                type="email"
                value={form.email}
                onChange={(v) => change("email", v)}
              />
              <AdminInput
                id="channel-address"
                label="Dirección"
                value={form.address}
                onChange={(v) => change("address", v)}
              />
              <AdminTextarea
                id="channel-description"
                label="Descripción"
                value={form.description}
                onChange={(e) => change("description", e.target.value)}
              />
            </div>
          </details>
        </>
      ),
    },
    {
      id: "hours",
      title: "Horario",
      description: "Días y horas guardados para este canal.",
      content: (
        <>
          <label>Horario del canal
            <select aria-label="Horario del canal" value={form.businessHoursId} onChange={event => change("businessHoursId", event.target.value)}>
              <option value="">Sin horario asignado</option>
              {loaded.hours.map(hour => <option key={hour.businessHoursId} value={hour.businessHoursId}>{hour.name}</option>)}
            </select>
          </label>
          <p>{schedule.text}</p>
          {schedule.detail && (
            <dl className="channelScheduleRows">
              {schedule.detail.split("\n").map((line) => {
                const [day, ...time] = line.split(": ");
                return (
                  <div key={day}>
                    <dt>{day}</dt>
                    <dd>{time.join(": ")}</dd>
                  </div>
                );
              })}
            </dl>
          )}
          <p className="channelHelp">
            El horario se edita en Configuración → Horarios. Se conservan las
            franjas y los días de ese horario.
          </p>
        </>
      ),
    },
    {
      id: "payments",
      title: "Métodos de pago",
      navTitle: "Pagos",
      description: "Métodos de pago que admite este canal de venta.",
      content: (
        <>
          {methods(loaded.payments, "paymentMethodIds", "paymentMethodId")}
          <p className="channelHelp">
            Los métodos disponibles se definen en Configuración → Métodos de
            pago.
          </p>
        </>
      ),
    },
    {
      id: "delivery",
      title: "Métodos de entrega y envío",
      navTitle: "Entrega",
      description: "Cómo pueden recibir el pedido los clientes de este canal.",
      content: (
        <>
          {methods(loaded.deliveries, "shippingMethodIds", "shippingMethodId")}
          <p className="channelHelp">
            La configuración de cada método se define en Configuración → Métodos
            de entrega y envío.
          </p>
        </>
      ),
    },
    {
      id: "prices",
      title: "Variables de precio",
      content: (
        <AdminInput id="channel-price-percentage" type="number" min={-100} step="any" required
          label="Variación de precio (%)" value={form.pricePercentage}
          onChange={value => change("pricePercentage", value)}
          description="Al guardar, el catálogo y el porcentaje se aplican a las nuevas ventas de este canal. 0 mantiene el precio. Se aplica a producto, variante y extras antes del IVA; los pedidos confirmados conservan sus importes."
        />
      ),
    },
    {
      id: "products",
      title: "Productos",
      description: "Productos del catálogo asignado a este canal de venta.",
      content: (
        <>
          <div className="channelProductsToolbar">
            <label>
              Catálogo
              <select
                aria-label="Catálogo del canal"
                value={form.catalogId}
                onChange={(e) => change("catalogId", e.target.value)}
              >
                <option value="">Sin catálogo asignado</option>
                {catalogs.map((c) => (
                  <option key={c.catalogId} value={c.catalogId}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="channelProductSearch">
              <LuSearch aria-hidden="true" />
              <span className="visually-hidden">
                Buscar productos del canal
              </span>
              <input
                type="search"
                placeholder="Buscar por nombre o referencia"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
          </div>
          <p className="channelHelp">
            {selectedProducts.length} productos. Para añadir o retirar
            productos, edita el catálogo. Cambiar el catálogo aquí se aplica al
            guardar el canal.
          </p>
          {/* Keyboard access is required to scroll the product table on narrow screens. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
          <div
            className="channelProductsRegion"
            role="region"
            aria-label="Productos del catálogo del canal"
            tabIndex={0}
          >
            <table>
              <thead>
                <tr>
                  <th>Referencia</th>
                  <th>Imagen</th>
                  <th>Nombre</th>
                  <th>Precio base con IVA</th>
                  <th>Estado del producto</th>
                </tr>
              </thead>
              <tbody>
                {visibleProducts.map((p) => {
                  const price = productPriceWithTax(p);
                  return (
                    <tr key={p.productId}>
                      <td>{p.reference || "—"}</td>
                      <td>
                        {typeof p.images?.[0] === "string" && p.images[0] ? (
                          <img src={p.images[0]} alt="" />
                        ) : (
                          <LuImageOff aria-label="Sin imagen" />
                        )}
                      </td>
                      <th scope="row">{p.name}</th>
                      <td>
                        {price == null
                          ? "—"
                          : new Intl.NumberFormat("es-ES", {
                              style: "currency",
                              currency: "EUR",
                            }).format(price)}
                      </td>
                      <td>
                        {p.status === "PUBLISHED" &&
                        p.isActive !== false &&
                        p.isActive !== 0
                          ? "Publicado y activo"
                          : "No publicado o inactivo"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!visibleProducts.length && <p>No hay productos que mostrar.</p>}
          </div>
        </>
      ),
    },
  ];
  if (loaded.row.type !== "ONLINE") sections.push({
    id: "terminals", title: "Terminales y usuarios", navTitle: "Terminales",
    description: "Puestos TPV, usuarios asignados y canales visibles.",
    content: <ChannelTerminals channelId={id} onPending={value => { lock.current = value; setBusy(value); }} />,
  });
  return (
    <div className="channelReferenceEditor" aria-busy={busy}>
      {header}
      <form id="channel-reference-form" ref={formRef} onSubmit={save}>
        {loaded.row.type !== "ONLINE" && !loaded.row.configurationEnabled && <p className="channelHelp">Al guardar, este TPV venderá los productos del catálogo que elijas y aplicará su variación de precio a las nuevas ventas.</p>}
        <fieldset disabled={busy}>
          <EditorSections sections={sections} />
        </fieldset>
        <div className="channelReferenceStatus">
          {error && <p role="alert">{error}</p>}
          {saved && <p role="status">Canal guardado y verificado</p>}
        </div>
      </form>
    </div>
  );
};
